// Lecturas del torneo para usuarios autenticados.

import { gql } from 'graphql-request';
import { getGraphQLQueryClient } from '../query-client';
import {
  Nullable,
  Tournament,
  UserWithToken,
  WithData,
  WithMeta,
} from '../api.types';
import { USER_BASIC_FIELDS, USER_WITH_COSSY, USER_WITH_GAME_IDS } from '../fragments';

export const listTournaments = async (
  user: Nullable<UserWithToken>,
  filters: Record<string, unknown>
) =>
  getGraphQLQueryClient(user).request<WithMeta<Tournament>>(
    gql`
      query ($filters: TournamentFilters) {
        data: tournaments(filters: $filters) {
          data {
            id
            title
            featured
            dateFrom
            dateTo
            konamiId

            rounds
            currentRound
            resultsValidated
            totalPlayers

            status
            deletedAt

            players {
              id
              status
              deckName
              user {
                id
                cossyId
              }
            }

            results {
              id
              userId
              position
              points
            }

            format {
              id
              name
            }

            organizer {
              id
              name
              logoUrl

              store {
                id
                name
              }
            }

            withTop

            topTournaments {
              id
              status
            }

            leagues {
              id
              league {
                id
                name
              }
            }
          }
          meta {
            total
            perPage
            lastPage
          }
        }
      }
    `,
    { filters }
  );

export const getTournamentPlayers = async (
  user: Nullable<UserWithToken>,
  variables: Record<string, unknown>
) =>
  getGraphQLQueryClient(user).request<WithMeta<Tournament>>(
    gql`
      query tournamentPlayers($filters: TournamentFilters) {
        data: tournamentPlayers(filters: $filters) {
          data {
            id
            title
            dateFrom
            dateTo
          }
          meta {
            total
            perPage
            lastPage
          }
        }
      }
    `,
    variables
  );

export const getTournament = async (
  user: Nullable<UserWithToken>,
  tournamentId: number
) =>
  getGraphQLQueryClient(user).request<WithData<Tournament>>(
    gql`
      ${USER_BASIC_FIELDS}
      ${USER_WITH_COSSY}
      ${USER_WITH_GAME_IDS}
      query tournament($id: Int!) {
        data: tournament(id: $id) {
          id
          title
          description
          featured
          currentRound
          rounds
          tournamentType
          status
          konamiId
          mainEventId
          publicEventPrizesDeliveredAt
          discordChannelId
          discordDecklistChannelId
          discordDecklistLogChannelId
          withDecklist
          duelingBookTourneyId
          playedOnDuelingBook
          recordReplays
          replayThinkingCapSeconds
          replayHandsPanel
          sleeveId
          playmatId
          organizerId
          gameTypeId
          platform {
            id
            slug
          }
          organizers {
            id
            organizerId
            isPrimary
            organizer {
              id
              name
              slug
              store {
                id
              }
            }
          }
          withTop
          thirdPlaceMatch
          grandFinalReset
          topType
          challongeId
          topdeckId
          limitlessId
          ktsFilePath
          streamMatchId

          formatId
          format {
            id
            name
          }
          banlistId

          tier

          location {
            id
            name
            address
            latitude
            longitude
            zipCode
          }

          locationId
          resultsValidated
          bestOf
          revealAllRounds
          enforceGameReporting
          hasStreaming

          createdAt
          createdByUserId
          createdBy {
            id
            name
            lastName
          }

          dateFrom
          dateTo

          acceptsOnlineRegistration
          nicknamesAllowed
          visibility
          inscriptionFee
          inscriptionDateFrom
          inscriptionDateTo

          preInscriptionFee
          preInscriptionDateFrom
          preInscriptionDateTo

          isTeamTournament
          playersPerTeam
          formatPositionAId
          formatPositionBId
          formatPositionCId
          formatPositionDId
          formatPositionEId

          judges {
            id
            userId
            tournamentId
            position
            description
            status
            user {
              ...UserBasicFields
            }
            judge {
              id
              rc
              pc
              dc
            }
          }

          tournamentTeams {
            id
            name
            position
            wins
            losses
            draws
            points
            dropRound
            players {
              id
              position
              deckName
              user {
                ...UserWithCossy
              }
            }
          }

          tournamentTeamMatches {
            id
            round
            tableNumber
            status
            team1Id
            team2Id
            winnerTeamId
            team1 {
              id
              name
              players {
                id
                position
                deckName
                user {
                  ...UserWithCossy
                }
              }
            }
            team2 {
              id
              name
              players {
                id
                position
                deckName
                user {
                  ...UserWithCossy
                }
              }
            }
            winnerTeam {
              id
              name
            }
            individualMatches {
              id
              round
              tableNumber
              matchNumber
              status
              replayUrl
              replayVideo {
                status
                seconds
                error
              }
              player1 {
                ...UserBasicFields
                duelingBookId
              }
              player2 {
                ...UserBasicFields
                duelingBookId
              }
              winner {
                ...UserBasicFields
              }
            }
          }

          players {
            id

            status
            invitedByOrganizer
            dropRound
            deckName
            metaDeckId
            fixedTable
            sleeveId

            user {
              ...UserWithGameIds
            }

            results {
              id
              userId
              position
              points
              wins
              losses
              draws
              tiebreaker
            }
          }

          matches {
            id
            round
            tableNumber
            isThirdPlace
            bracketSide
            bracketRound
            bracketPosition
            replayUrl
            replayVideo {
              status
              seconds
              error
            }
            status
            resultType
            startedAt
            reportedAt
            reportedBy {
              id
              name
              lastName
            }
            player1 {
              ...UserWithGameIds
              duelingBookId
            }
            player2 {
              ...UserWithGameIds
              duelingBookId
            }
            winner {
              ...UserWithGameIds
            }
            game1Winner {
              id
            }
            game2Winner {
              id
            }
            game3Winner {
              id
            }
            game4Winner {
              id
            }
            game5Winner {
              id
            }
          }

          results {
            id
            userId
            position
            points
            wins
            losses
            draws
            omwPercent
            oomwPercent
            lossSquaresSum
            tiebreaker
            player {
              id
              user {
                ...UserWithGameIds
              }
            }
          }

          format {
            id
            name
            slug
          }

          organizer {
            id
            name
            logoUrl

            store {
              id
            }
          }

          links {
            type
            url
            label
          }

          prizes {
            id
            prizeType
            positionFrom
            positionTo
            prize
            minPlayers
            isCredit
            creditAmount
          }

          files {
            id
            tournamentId
            fileName
            originalName
            fileSize
            mimeType
            url
            createdAt
          }

          leagues {
            leagueId
            roundNumber

            league {
              id
              name
              organizerId
              type
              dateFrom
              dateTo

              rules {
                id
                rule
              }
            }
          }

          swissId

          bountyUserIds

          roundPlannings {
            id
            tournamentId
            round
            startDate
            endDate
            actualStartDate
            duration
          }

          topTournaments {
            id
            title
            status
            tournamentType
            rounds
            currentRound
            withTop
            thirdPlaceMatch
            grandFinalReset
            topType
            dateFrom
            dateTo
            isTeamTournament

            organizer {
              id
              name
              logoUrl
            }

            format {
              id
              name
            }

            players {
              id
              user {
                ...UserWithCossy
              }
            }

            tournamentTeams {
              id
              name
            }

            matches {
              id
              round
              tableNumber
              isThirdPlace
              bracketSide
              bracketRound
              bracketPosition
              status
              player1 {
                ...UserWithGameIds
              }
              player2 {
                ...UserWithGameIds
              }
              winner {
                ...UserWithGameIds
              }
            }

            results {
              id
              userId
              position
              points
              wins
              losses
              draws
              tiebreaker
              player {
                id
                user {
                  ...UserWithGameIds
                }
              }
            }
          }
        }
      }
    `,
    { id: tournamentId }
  );
